import { TxSearchResult, type TxSummary } from '@apm/contracts';
import { useMemo } from 'react';
import { toQuery } from '../api';
import { useQuery } from '../hooks/useQuery';
import { TX_TYPE_TONE } from '../live/format';
import { routeHref } from '../router';
import { STATUS_LEVEL_TEXT, statusLevel, statusText } from '../status';
import { TIP, TX_TYPE_LABEL } from '../terms';
import { formatCount, formatDuration, formatFull, formatStamp } from '../time';
import { Cell, Chip, type Column, EmptyState, ErrorText, Loading, Row, Table } from '../ui';

/**
 * 같은 사용자의 앞뒤 요청 (22-B회차, 빠진 정보 4).
 *
 * "이 오류 직전에 사용자가 무엇을 했나"는 상세 한 장으로는 알 수 없다. 사용자 id는 수집하지
 * 않으므로(2026-09-07 결정 2) 같은 클라이언트 IP + 같은 User-Agent를 같은 사용자로 본다.
 * 검색 API의 `clientAddress`(동등)·`userAgent`(부분일치) 조건을 그대로 쓰고, User-Agent가
 * 부분일치라 화면에서 한 번 더 정확히 맞춘다. 서버는 손대지 않았다.
 *
 * 23-A회차에 **표시 상한 30건**이 붙었다(UI 피드백 14). 바쁜 시간대에는 앞뒤 1분에도 수백 건이
 * 쌓여 상세 화면이 그 표 하나가 된다. 잘린 것은 "요청 검색에서 더 보기"로 넘긴다 —
 * 검색은 페이징을 이미 갖고 있다.
 *
 * **앞뒤를 따로 부른다** (23-A회차 리뷰 5). 한 번에 앞뒤 2분을 최신순 200건으로 받아 자르면,
 * 이 요청 뒤에 200건이 몰린 구간에서 앞쪽을 한 건도 못 받는다. 앞 창은 최신순(가까운 것부터),
 * 뒤 창은 오래된 순(역시 가까운 것부터)으로 각각 받아 **정말 가까운 것**을 고른다.
 * 앞뒤 건수도 각 응답의 `total`이라 표에 그린 수와 무관하게 맞는다.
 */

/** 앞뒤로 보는 폭 */
const NEARBY_WINDOW_MS = 60_000;
/** 한쪽에서 표에 그리는 최대 건수 */
export const NEARBY_SIDE_ROWS = 15;
/**
 * 한쪽에서 받아 오는 건수. 서버는 User-Agent를 부분일치로 보고 실시간 연결도 함께 주므로
 * 화면에서 거르고 나면 줄어든다. 넉넉히 받아 두고 거른 뒤에 자른다
 */
const SIDE_FETCH = NEARBY_SIDE_ROWS * 3;

const COLUMNS: readonly Column[] = [
  { label: '시작', width: 'w-40' },
  { label: '', width: 'w-16' },
  { label: '유형', width: 'w-28' },
  { label: '요청 경로', title: TIP.entry },
  { label: '응답시간', width: 'w-28' },
  { label: '상태', width: 'w-32' },
];

/**
 * 한쪽 조회 주소. IP가 없으면 같은 사용자를 가려낼 수 없어 부르지 않는다.
 *
 * 두 창은 이 요청의 시작 시각을 **비켜 간다**(앞은 `…, t-1]`, 뒤는 `[t+1, …`). 겹치지 않아야
 * 각 응답의 `total`을 그대로 "앞 n건 · 뒤 m건"으로 쓸 수 있다. 같은 사용자가 정확히 같은 ms에
 * 보낸 다른 요청은 어느 쪽에도 안 들어오지만, IP·브라우저가 같고 ms까지 같은 일은 사실상 없다.
 */
export function nearbyUrl(current: TxSummary, side: 'before' | 'after'): string | undefined {
  if (current.clientAddress === undefined) return undefined;
  const at = current.startedAt;
  const window =
    side === 'before'
      ? { from: at - NEARBY_WINDOW_MS, to: at - 1, sort: undefined }
      : { from: at + 1, to: at + NEARBY_WINDOW_MS, sort: 'startedAtAsc' };
  return `/api/tx/search${toQuery({
    from: window.from,
    to: window.to,
    // 앞 창은 기본 정렬(최신순)이 곧 가까운 순이다. 뒤 창은 오래된 순이어야 가까운 순이 된다
    sort: window.sort,
    clientAddress: current.clientAddress,
    userAgent: current.userAgent,
    limit: SIDE_FETCH,
  })}`;
}

/** 같은 사용자로 볼 행만. 서버의 User-Agent는 부분일치라 여기서 정확히 맞추고 sse는 뺀다 */
function sameUser(rows: readonly TxSummary[], current: TxSummary): TxSummary[] {
  return rows
    .filter((row) => current.userAgent === undefined || row.userAgent === current.userAgent)
    .filter((row) => row.txType !== 'sse')
    .filter((row) => row.txId !== current.txId);
}

export interface Nearby {
  /** 시각 오름차순. 가운데에 이 요청이 있다 */
  rows: TxSummary[];
  /** 앞 창에서 그린 수와 서버가 센 전체 수 */
  shownBefore: number;
  before: number;
  /** 뒤 창에서 그린 수와 서버가 센 전체 수 */
  shownAfter: number;
  after: number;
  /** 그리지 않은 수. 서버가 센 전체에서 그린 것을 뺀다 */
  hidden: number;
}

/**
 * 두 응답 → 시각순 한 목록.
 *
 * `beforeRows`는 최신순(가까운 것부터), `afterRows`는 오래된 순(역시 가까운 것부터)으로 온다.
 * 각각 가까운 쪽에서 `sideLimit`건을 고르고 시각순으로 세운다.
 * `beforeTotal`·`afterTotal`은 서버가 센 값이라 표에 그린 수와 상관없이 맞는다.
 */
export function arrangeNearby(
  input: {
    beforeRows: readonly TxSummary[];
    beforeTotal: number;
    afterRows: readonly TxSummary[];
    afterTotal: number;
  },
  current: TxSummary,
  sideLimit: number = NEARBY_SIDE_ROWS,
): Nearby {
  const before = sameUser(input.beforeRows, current).slice(0, sideLimit);
  const after = sameUser(input.afterRows, current).slice(0, sideLimit);
  const rows = [...before, current, ...after].sort(
    (a, b) => a.startedAt - b.startedAt || a.txId.localeCompare(b.txId),
  );
  const shownBefore = before.length;
  const shownAfter = after.length;
  return {
    rows,
    shownBefore,
    before: input.beforeTotal,
    shownAfter,
    after: input.afterTotal,
    // 서버가 센 값에서 그린 것을 뺀다. 화면에서 거른 행(다른 브라우저·실시간 연결)도 여기 든다
    hidden:
      Math.max(0, input.beforeTotal - shownBefore) + Math.max(0, input.afterTotal - shownAfter),
  };
}

/**
 * 같은 조건을 요청 검색 화면의 주소로. 상한에 걸린 사람이 이어서 볼 곳이다 (23-A회차).
 * 기간은 앞뒤 1분 그대로이고, IP·브라우저(User-Agent)까지 채운다
 */
export function nearbySearchQuery(current: TxSummary): string {
  const params = new URLSearchParams({
    from: String(current.startedAt - NEARBY_WINDOW_MS),
    to: String(current.startedAt + NEARBY_WINDOW_MS),
  });
  if (current.clientAddress !== undefined) params.set('clientAddress', current.clientAddress);
  if (current.userAgent !== undefined) params.set('userAgent', current.userAgent);
  return params.toString();
}

export function NearbyRequests({ current }: { current: TxSummary }) {
  const beforeUrl = nearbyUrl(current, 'before');
  const afterUrl = nearbyUrl(current, 'after');
  const before = useQuery(beforeUrl, TxSearchResult);
  const after = useQuery(afterUrl, TxSearchResult);
  const loading = before.loading || after.loading;
  const error = before.error ?? after.error;
  const truncated = before.data?.truncated === true || after.data?.truncated === true;
  const arranged = useMemo(
    () =>
      before.data === undefined || after.data === undefined
        ? undefined
        : arrangeNearby(
            {
              beforeRows: before.data.rows,
              beforeTotal: before.data.total,
              afterRows: after.data.rows,
              afterTotal: after.data.total,
            },
            current,
          ),
    [before.data, after.data, current],
  );

  if (beforeUrl === undefined) {
    return (
      <EmptyState
        title="클라이언트 IP가 없어 같은 사용자를 가려낼 수 없다"
        hint="정기 작업이나 공개 경로(실시간 연결)는 IP가 남지 않는다"
      />
    );
  }
  if (error !== undefined) return <ErrorText>{error}</ErrorText>;
  if (loading || arranged === undefined) return <Loading />;
  if (arranged.rows.length <= 1) {
    return (
      <EmptyState
        title="앞뒤 1분에 같은 사용자의 다른 요청이 없다"
        hint={`${current.clientAddress ?? ''} 기준. 브라우저(User-Agent)까지 같아야 센다`}
      />
    );
  }

  return (
    <div className="space-y-2">
      <p className="tabular flex flex-wrap items-center gap-x-2 whitespace-pre-line text-xs text-muted">
        <span>
          앞 {formatCount(arranged.before)}건 · 뒤 {formatCount(arranged.after)}건 ·{' '}
          <span className="font-mono">{current.clientAddress}</span>
          {truncated ? ` · ${TIP.truncated}` : ''}
        </span>
        {arranged.hidden > 0 ? (
          <span>
            가까운 것부터 앞 {formatCount(arranged.shownBefore)}건 · 뒤{' '}
            {formatCount(arranged.shownAfter)}건만 표시한다 ({formatCount(arranged.hidden)}건 숨김).
          </span>
        ) : null}
        <a
          href={routeHref({ name: 'requests', query: nearbySearchQuery(current) })}
          className="text-link no-underline hover:underline"
        >
          요청 검색에서 더 보기
        </a>
      </p>
      <Table columns={COLUMNS} caption="같은 사용자의 앞뒤 요청" minWidth="min-w-[860px]">
        {arranged.rows.map((row) => {
          const me = row.txId === current.txId;
          return (
            <Row key={row.txId}>
              <Cell nowrap tone="muted" className={`tabular ${me ? 'font-semibold text-ink' : ''}`}>
                {me ? (
                  <span title={formatFull(row.startedAt)}>{formatStamp(row.startedAt)}</span>
                ) : (
                  <a
                    href={routeHref({
                      name: 'detail',
                      traceId: row.traceId,
                      service: row.service,
                    })}
                    title={formatFull(row.startedAt)}
                    className="-mx-1 block rounded px-1 text-link no-underline hover:underline"
                  >
                    {formatStamp(row.startedAt)}
                  </a>
                )}
              </Cell>
              <Cell nowrap>{me ? <Chip tone="http">이 요청</Chip> : null}</Cell>
              <Cell>
                <Chip tone={TX_TYPE_TONE[row.txType]}>{TX_TYPE_LABEL[row.txType]}</Chip>
              </Cell>
              <Cell
                mono
                title={row.entry}
                className={`max-w-[360px] truncate ${me ? 'font-semibold' : ''}`}
              >
                {row.entry}
              </Cell>
              <Cell mono nowrap className="tabular">
                {formatDuration(row.durationMs)}
              </Cell>
              <Cell nowrap className={STATUS_LEVEL_TEXT[statusLevel(row)]}>
                {statusText(row)}
              </Cell>
            </Row>
          );
        })}
      </Table>
    </div>
  );
}
